import * as yup from 'yup';
import i18n from '../../../../i18n';

import { PK_FORMAT_REGEX } from '../../../../utils/transform';
import { pkDentroDeLaCarretera } from '../../../../utils/pkValidation';
import { parsePk } from '../../../../utils/transform';
import { validateSegmentCorrection } from '../../../../utils/segmentCorrection';
import { RouteSegment } from '../../../../types/surveillanceRoute';
import type { SegmentEditorMode } from '../../../../utils/surveillanceRoute';

/**
 * Schema de «Corregir el tramo» (2026-08-10).
 *
 * Este modal era el único de los cuatro del tramo que NO usaba react-hook-form:
 * llevaba siete `useState` y un único mensaje de error al pie, con los códigos
 * clasificados a mano para saber qué campo pintar en rojo. Eso fallaba en
 * silencio en cuanto alguien añadía un código nuevo sin clasificarlo.
 *
 * Ahora sigue el patrón de los otros 135 formularios de la app: la regla vive
 * AQUÍ y react-hook-form ata cada error a su campo por el nombre.
 */

/** Campos ocultos que el modal siembra para que los tests tengan contexto. */
type Ocultos = {
  pkInitRoad?: string;
  pkEndRoad?: string;
  endRoadPkInit?: string;
  endRoadPkEnd?: string;
  segments?: RouteSegment[];
  segmentId?: string;
  /**
   * La carretera ELEGIDA en el selector, no la guardada en el tramo. Es lo que
   * permite que cambiar de vía quite el aviso de solape en el momento.
   */
  roadId?: string | null;
  /** Las carreteras marcadas en el selector (con más de una, el tramo cruzó). */
  roadIds?: string[] | null;
};

export interface EditSegmentFormData extends Ocultos {
  pkInit: string;
  pkEnd: string;
  direction: string;
  lane: string;
  startDate: string;
  endDate: string;
  pauseCauseDetail?: string;
}

/**
 * Corre `validateSegmentCorrection` (solapes con los tramos vecinos y
 * coherencia PK↔sentido) y devuelve su código de error, o `null`.
 *
 * Se hace desde el schema y no en el submit para que el error quede atado a un
 * campo y lo marque en rojo: hasta hoy estos avisos salían en un mensaje suelto
 * al pie, sin decir cuál de los siete campos había que tocar.
 */
const problemaDeCorreccion = (parent: EditSegmentFormData): string | null => {
  const init = parsePk(parent.pkInit ?? '');
  const end = parsePk(parent.pkEnd ?? '');
  // Sin PK parseables no hay nada que cruzar con los vecinos: de eso ya avisa
  // el `matches` del formato en su propio campo.
  if (!init || !end || !parent.segments || !parent.segmentId) return null;
  if (!parent.startDate || !parent.endDate) return null;

  return validateSegmentCorrection(parent.segments, parent.segmentId, {
    pkInitKm: init.km,
    pkInitMeter: init.meter,
    pkEndKm: end.km,
    pkEndMeter: end.meter,
    direction: parent.direction,
    startDate: parent.startDate,
    endDate: parent.endDate,
    // 🔴 La carretera ELEGIDA en el modal, no la guardada: cambiar de vía tiene
    // que quitar el aviso de solape en el momento, sin guardar. Sin esto, el
    // corte por carretera del validador no sirve de nada aquí — compararía el
    // borrador contra sus vecinos como si todos fueran de la misma vía.
    roadId: parent.roadId ?? null,
    roadIds: parent.roadIds ?? null,
  });
};

const ES_DE_TIEMPO = (codigo: string): boolean => codigo.includes('_TIME_');

/**
 * El schema depende del MODO porque el operario tiene PK y horas
 * DESHABILITADOS (spec 2026-08-15).
 *
 * 🔴 Sin esto la función quedaría inservible justo donde más falta hace. El
 * `pk-validation` comprueba el PK contra los extremos de la carretera **aunque
 * el usuario no lo toque**, y hay tramos históricos con el PK fuera de rango
 * (el caso `300+100` de la C-25, cazado el 2026-08-10 y arreglado solo hacia
 * adelante, sin backfill). Con la regla encendida, el operario de uno de esos
 * tramos no podría guardar JAMÁS un cambio de carril: el formulario se
 * bloquearía por un campo gris, señalando en rojo algo que él no puede editar.
 *
 * Lo mismo con el solapamiento y las horas: son reglas sobre valores que en
 * este modo son de solo lectura, así que ni pueden ser culpa suya ni las puede
 * arreglar.
 */
export const buildEditSegmentSchema = (
  mode: SegmentEditorMode
): yup.ObjectSchema<EditSegmentFormData> => {
  const esOperario = mode === 'operator';

  const pkInit = esOperario
    ? yup.string().defined()
    : yup
        .string()
        .matches(PK_FORMAT_REGEX, () => i18n.t('validation:pk.format'))
        .test('pk-validation', function (value) {
          if (pkDentroDeLaCarretera(value, this.parent)) return true;
          return this.createError({ message: 'validation:pk.validation' });
        })
        // Los solapes/coherencia cuelgan del PK inicial, que es el campo que el
        // usuario mueve para resolverlos.
        .test('segment-correction-pk', function () {
          const problema = problemaDeCorreccion(this.parent);
          if (!problema || ES_DE_TIEMPO(problema)) return true;
          return this.createError({ message: problema });
        })
        .required(() => i18n.t('validation:required'));

  const pkEnd = esOperario
    ? yup.string().defined()
    : yup
        .string()
        .matches(PK_FORMAT_REGEX, () => i18n.t('validation:pk.format'))
        .test('pk-validation', function (value) {
          // 🔴 El PK FINAL se valida contra la vía donde el tramo ACABA, que
          // en uno que cruzó no es la misma que la del principio: empieza en
          // la troncal (82+800) y termina en el ramal (236+500). Con un solo
          // rango para los dos extremos siempre sobraba uno, y un tramo
          // multi-carretera no se podía corregir — «El PK no es válido» hiciera
          // lo que hiciera el usuario (2026-08-24).
          //
          // Sin cruce no hay rango de fin y se cae al de siempre: los tramos de
          // una sola vía se comportan exactamente igual que antes.
          const extremos = {
            pkInitRoad: this.parent.endRoadPkInit || this.parent.pkInitRoad,
            pkEndRoad: this.parent.endRoadPkEnd || this.parent.pkEndRoad,
          };
          if (pkDentroDeLaCarretera(value, extremos)) return true;
          return this.createError({ message: 'validation:pk.validation' });
        })
        .required(() => i18n.t('validation:required'));

  const startDate = esOperario
    ? yup.string().defined()
    : yup
        .string()
        .required(() => i18n.t('validation:required'))
        // Los problemas de HORAS marcan la fecha de inicio.
        .test('segment-correction-time', function () {
          const problema = problemaDeCorreccion(this.parent);
          if (!problema || !ES_DE_TIEMPO(problema)) return true;
          return this.createError({ message: problema });
        });

  const endDate = esOperario
    ? yup.string().defined()
    : yup.string().required(() => i18n.t('validation:required'));

  return yup.object().shape({
    pkInit,
    pkEnd,

    direction: yup.string().defined(),
    lane: yup.string().defined(),

    startDate,
    endDate,

    pauseCauseDetail: yup.string(),

    // Ocultos: contexto para los tests de arriba, no los rellena el usuario.
    // `pkInitRoad`/`pkEndRoad` son los extremos de la vía donde EMPIEZA el
    // tramo; `endRoadPk*`, los de la vía donde acaba (solo si cruzó).
    pkInitRoad: yup.string(),
    pkEndRoad: yup.string(),
    endRoadPkInit: yup.string(),
    endRoadPkEnd: yup.string(),
    segments: yup.mixed<RouteSegment[]>(),
    segmentId: yup.string(),
    roadId: yup.string().nullable(),
    roadIds: yup.mixed<string[]>().nullable(),
  }) as yup.ObjectSchema<EditSegmentFormData>;
};
